import React, { useState, useEffect } from "react";
import {
  Search, MessageSquare, Heart, X, Loader2,
  Image as ImageIcon, LayoutGrid, Images, AlertTriangle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import PageSeo from "../SEO/PageSeo";
import {
  SHOP_CATEGORIES, API_URL, buildTheme, formatName, categoryLabel,
  isVerifiedListing, timeAgo, toTitleCase, InlineSidebarChat, PremiumMessagingCenter,
} from "./marketplaceShared";

export default function MarketplacePage({ isDarkMode, toggleTheme, seoKeywords }) {
  const primaryKeyword = toTitleCase(seoKeywords?.[0]) || "Pilot Car Marketplace";
  const secondaryKeyword = toTitleCase(seoKeywords?.[2]) || "Pilot Car Dispatch Software";
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [shopListings, setShopListings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Chat is now fully independent of "viewing details" — clicking a card
  // always goes to the dedicated product page; only the Message button opens chat.
  const [chatItem, setChatItem] = useState(null);
  const [showDesktopChat, setShowDesktopChat] = useState(false);
  const [showMobileChat, setShowMobileChat] = useState(false);
  const [savedItems, setSavedItems] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [viewMode, setViewMode] = useState("gallery");
  const [visibleCount, setVisibleCount] = useState(24);

  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "error",
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchShopListings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/tradelink/listings/`);
        if (response.ok) {
          const data = await response.json();
          const list = Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []);
          setShopListings(list);
        } else { setShopListings([]); }
      } catch (error) {
        console.error("Error fetching TradeLink data:", error);
        setShopListings([]);
      } finally { setIsLoading(false); }
    };
    fetchShopListings();
  }, []);

  useEffect(() => { setVisibleCount(24); }, [activeCategory, searchQuery]);

  const displayedItems = (Array.isArray(shopListings) ? shopListings : []).filter((item) => {
    const matchesCategory = activeCategory === "All" || item.category === activeCategory;
    const title = item?.title || "";
    const matchesSearch = title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const visibleItems = displayedItems.slice(0, visibleCount);

  // ── JSON-LD SCHEMA GENERATION FOR CRAWLERS ──
  const marketplaceSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${primaryKeyword} - Equipment & Escort Gear`,
    "description": `Browse certified pilot car equipment, oversize load safety gear, and pilot vehicles on PilotLink USA TradeLink.`,
    "url": "https://pilotlinkusa.org/marketplace",
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": displayedItems.length,
      "itemListElement": visibleItems.slice(0, 10).map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Product",
          "name": item.title,
          "description": item.description || item.title,
          "image": item.images?.[0]?.image || "https://pilotlinkusa.org/logo192.png",
          "offers": {
            "@type": "Offer",
            "price": item.price,
            "priceCurrency": "USD",
            "availability": "https://schema.org/InStock",
            "seller": { "@type": "Person", "name": formatName(item.seller) },
          },
        },
      })),
    },
  };

  const toggleSave = (e, id) => {
    e.stopPropagation();
    setSavedItems((prev) => prev.includes(id) ? prev.filter((fId) => fId !== id) : [...prev, id]);
  };

  // Clicking a card (or "Details") goes to the real product page — pass the
  // item via router state for an instant render; the detail page still
  // re-fetches by id so direct links and refreshes work too.
  const goToListing = (item) => {
    navigate(`/marketplace/listing/${item.id}`, { state: { listing: item } });
  };

  const triggerSubscriptionModal = () => {
    setShowDesktopChat(false);
    setShowMobileChat(false);
    setModalConfig({
      isOpen: true,
      title: "Action Required",
      message: "Your free trial has expired or your membership is inactive. Please set up your automated monthly billing to continue using the messaging and posting features.",
      type: "subscription",
    });
  };

  const handleMsgClick = (e, item) => {
    e.stopPropagation();
    const token = localStorage.getItem("token");
    if (!token) { navigate("/login"); return; }
    setChatItem(item);
    if (isMobile) setShowMobileChat(true);
    else setShowDesktopChat(true);
  };

  const handlePostListingClick = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login", { state: { redirectTo: "/tradelink/post-listing" } });
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/tradelink/chat/inbox/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 403) {
        const data = await res.json();
        if (data.code === "REQUIRES_SUBSCRIPTION" || data.detail?.includes("membership")) {
          triggerSubscriptionModal();
          return;
        }
      }
    } catch (err) {
      console.error("Permission check failed", err);
    }
    navigate("/tradelink/post-listing");
  };

  const theme = buildTheme(isDarkMode);
  const { D, bg, panelBg, midBg, border, text, textMid, textSoft, inputBg, amber, amberL } = theme;

  const pageTitle = `${primaryKeyword} - Buy & Sell Escort Equipment | PilotLink USA`;
  let pageDescription = `Browse the leading ${primaryKeyword}. Connect with verified escort drivers, trade gear, and leverage our ${secondaryKeyword} for oversize load transportation.`;
  let canonicalPath = "/marketplace";
  if (searchQuery) {
    canonicalPath = `/marketplace?q=${encodeURIComponent(searchQuery)}`;
  } else if (activeCategory !== "All") {
    const categoryName = categoryLabel(activeCategory);
    pageDescription = `Shop ${categoryName.toLowerCase()} and other certified pilot car equipment on the PilotLink USA TradeLink Marketplace.`;
    canonicalPath = `/marketplace?category=${activeCategory}`;
  }

  return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "'Inter',sans-serif", transition: "background .3s" }}>

      <PageSeo
        title={pageTitle}
        description={pageDescription}
        keywords={seoKeywords}
        canonicalUrl={canonicalPath}
        schema={{ ...marketplaceSchema, name: pageTitle, description: pageDescription }}
      />

      <Navbar isDarkMode={isDarkMode} toggleTheme={toggleTheme} />

      {/* HERO BANNER */}
      <div style={{ background: `linear-gradient(135deg,#0d1f3c 0%,#1a3358 60%,#0f2d56 100%)`, padding: "clamp(24px,4vw,40px) clamp(16px,4vw,40px)", borderBottom: "3px solid #f59e0b", position: "relative", overflow: "hidden" }}>
        <div aria-hidden style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(rgba(255,255,255,0.05) 1px,transparent 1px)", backgroundSize: "20px 20px", pointerEvents: "none" }} />
        <div aria-hidden style={{ position: "absolute", top: "-20%", right: "-5%", width: 280, height: 280, borderRadius: "50%", background: "radial-gradient(circle,rgba(251,191,36,0.12) 0%,transparent 70%)", pointerEvents: "none" }} />
        <div style={{ maxWidth: 1280, margin: "0 auto", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 16, position: "relative", zIndex: 1 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".14em", color: amberL, textTransform: "uppercase", marginBottom: 8 }}>Equipment Marketplace & Dispatch Tools</div>
            <h1 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(22px,4vw,34px)", fontWeight: 900, color: "#fff", margin: "0 0 8px", lineHeight: 1.15 }}>
              🛒 PilotLink <span style={{ color: amberL }}>TradeLink</span> Marketplace
            </h1>
            <p style={{ fontSize: 14, color: "rgba(255,255,255,0.65)", margin: 0 }}>Pilot car equipment, high-poles, strobe lights & vehicles — bought and sold by verified professionals</p>
          </div>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
            {[["✅", "Verified Sellers"], ["🔒", "Secure Messaging"], ["📍", "Location-Based"]].map(([ic, lb]) => (
              <div key={lb} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "rgba(255,255,255,0.70)", fontWeight: 500 }}>
                <span>{ic}</span>{lb}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "clamp(16px,3vw,28px) clamp(12px,3vw,24px)" }}>

        {/* SEARCH + POST */}
        <div style={{ display: "flex", gap: 10, marginBottom: 12, alignItems: "center" }}>
          <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
            <Search size={15} style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", color: textSoft, pointerEvents: "none" }} />
            <input type="text" placeholder={isMobile ? "Search equipment, lights, poles…" : "Search equipment, trucks, lights, height poles…"} value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: "100%", padding: "12px 36px 12px 38px", borderRadius: 12, border: `1.5px solid ${border}`, background: inputBg, color: text, fontSize: 15, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
              onFocus={(e) => e.target.style.borderColor = amber} onBlur={(e) => e.target.style.borderColor = border}
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: textSoft }}>
                <X size={14} />
              </button>
            )}
          </div>
          <button onClick={handlePostListingClick} style={{ display: "flex", alignItems: "center", gap: "6px", padding: isMobile ? "12px 16px" : "8px 16px", borderRadius: 12, border: `1.5px solid ${amber}`, background: `linear-gradient(135deg,${amber},${amberL})`, color: "#0d1f3c", fontWeight: 800, fontSize: 13, cursor: "pointer", textDecoration: "none", whiteSpace: "nowrap", fontFamily: "inherit", transition: "all .15s", boxShadow: `0 2px 10px rgba(245,158,11,0.35)`, flexShrink: 0 }}>
            ➕ {isMobile ? "Post" : "Post Listing"}
          </button>
        </div>

        {/* CATEGORY PILLS */}
        <div style={{ position: "relative", marginLeft: isMobile ? "clamp(-16px,-4vw,-12px)" : 0, marginRight: isMobile ? "clamp(-16px,-4vw,-12px)" : 0, marginBottom: 20 }}>
          <div className="hide-scrollbar" style={{ display: "flex", gap: 9, overflowX: "auto", flexWrap: "nowrap", scrollSnapType: "x proximity", WebkitOverflowScrolling: "touch", padding: isMobile ? "2px clamp(16px,4vw,12px) 6px" : "2px 0 6px" }}>
            {SHOP_CATEGORIES.map((c) => {
              const active = activeCategory === c.id;
              return (
                <button key={c.id} onClick={() => setActiveCategory(c.id)} style={{ padding: isMobile ? "11px 18px" : "8px 16px", minHeight: isMobile ? 42 : "auto", borderRadius: 99, border: `1.5px solid ${active ? amber : border}`, background: active ? `linear-gradient(135deg,${amber},${amberL})` : panelBg, color: active ? "#0d1f3c" : textMid, fontWeight: active ? 800 : 600, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap", fontFamily: "inherit", transition: "all .15s", boxShadow: active ? `0 2px 10px rgba(245,158,11,0.35)` : "none", scrollSnapAlign: "start", flexShrink: 0 }}>
                  {c.label}
                </button>
              );
            })}
          </div>
          <div style={{ position: "absolute", top: 0, bottom: 6, left: 0, width: isMobile ? 24 : 0, background: `linear-gradient(90deg, ${bg}, transparent)`, pointerEvents: "none" }} />
          <div style={{ position: "absolute", top: 0, bottom: 6, right: 0, width: isMobile ? 28 : 0, background: `linear-gradient(270deg, ${bg}, transparent)`, pointerEvents: "none" }} />
        </div>

        {!isLoading && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: textSoft, fontWeight: 600, letterSpacing: ".02em" }}>
              {displayedItems.length > 0 ? `${displayedItems.length} listing${displayedItems.length !== 1 ? "s" : ""} found` : "No listings match your search"}
            </div>
            <div style={{ display: "flex", gap: 4, padding: 3, borderRadius: 10, background: midBg, border: `1px solid ${border}`, flexShrink: 0 }}>
              <button onClick={() => setViewMode("gallery")} aria-label="Gallery view" title="Gallery view"
                style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 10px", borderRadius: 7, border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: 700, transition: "all .15s", background: viewMode === "gallery" ? panelBg : "transparent", color: viewMode === "gallery" ? amber : textSoft, boxShadow: viewMode === "gallery" ? "0 1px 4px rgba(0,0,0,0.12)" : "none" }}>
                <Images size={14} /> {!isMobile && "Gallery"}
              </button>
              <button onClick={() => setViewMode("grid")} aria-label="Grid view" title="Grid view"
                style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 10px", borderRadius: 7, border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: 700, transition: "all .15s", background: viewMode === "grid" ? panelBg : "transparent", color: viewMode === "grid" ? amber : textSoft, boxShadow: viewMode === "grid" ? "0 1px 4px rgba(0,0,0,0.12)" : "none" }}>
                <LayoutGrid size={14} /> {!isMobile && "Grid"}
              </button>
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>

          {/* GRID */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {isLoading ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 24px", background: panelBg, borderRadius: 16, border: `1px solid ${border}` }}>
                <Loader2 size={36} style={{ color: amber, animation: "spin 1s linear infinite", marginBottom: 12 }} />
                <div style={{ fontSize: 14, color: textMid, fontWeight: 600 }}>Loading listings…</div>
              </div>
            ) : displayedItems.length === 0 ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 24px", background: panelBg, borderRadius: 16, border: `1px solid ${border}`, textAlign: "center" }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>🛒</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: text, marginBottom: 8 }}>No listings yet</div>
                <div style={{ fontSize: 14, color: textMid, marginBottom: 24, maxWidth: 320 }}>Be the first to list pilot car equipment on PilotLink TradeLink.</div>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap", justifyContent: "center" }}>
                  {[["🔦", "Strobe Lights"], ["📻", "CB Radios"], ["🚗", "Pilot Vehicles"], ["🚩", "Oversize Signs"]].map(([ic, lbl]) => (
                    <div key={lbl} style={{ padding: "10px 16px", borderRadius: 10, background: midBg, border: `1px solid ${border}`, fontSize: 13, color: textMid, fontWeight: 600 }}>{ic} {lbl}</div>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: viewMode === "grid" ? (isMobile ? "repeat(3,1fr)" : `repeat(auto-fill,minmax(150px,1fr))`) : (isMobile ? "repeat(2,1fr)" : `repeat(auto-fill,minmax(240px,1fr))`), gap: viewMode === "grid" ? "clamp(8px,1.5vw,12px)" : "clamp(10px,2vw,18px)" }}>

                  {visibleItems.map((item) => {
                    const sellerName = formatName(item.seller);
                    const isSaved = savedItems.includes(item.id);
                    const compact = viewMode === "grid";
                    return (
                      <div key={item.id} onClick={() => goToListing(item)}
                        style={{ background: panelBg, borderRadius: compact ? 12 : 16, border: `2px solid ${border}`, overflow: "hidden", cursor: "pointer", transition: "all .18s", boxShadow: D ? "0 2px 8px rgba(0,0,0,0.20)" : "0 2px 8px rgba(13,31,60,0.07)" }}
                        onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = D ? "0 8px 24px rgba(0,0,0,0.30)" : "0 8px 24px rgba(13,31,60,0.12)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = D ? "0 2px 8px rgba(0,0,0,0.20)" : "0 2px 8px rgba(13,31,60,0.07)"; }}
                      >
                        {/* Image */}
                        <div style={{ height: compact ? "clamp(90px,14vw,120px)" : "clamp(140px,18vw,180px)", background: midBg, position: "relative", overflow: "hidden" }}>
                          {item.images?.length > 0
                            ? <img src={item.images[0].image} alt={item.title} style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform .4s" }} onMouseEnter={(e) => e.target.style.transform = "scale(1.05)"} onMouseLeave={(e) => e.target.style.transform = "none"} />
                            : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: compact ? 24 : 40 }}>📦</div>
                          }
                          {!compact && (
                            <button onClick={(e) => toggleSave(e, item.id)} style={{ position: "absolute", top: 10, right: 10, width: 32, height: 32, borderRadius: "50%", background: "rgba(0,0,0,0.40)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)", color: isSaved ? "#ef4444" : "#fff", fontSize: 15, transition: "all .15s" }}>
                              <Heart size={14} style={{ fill: isSaved ? "#ef4444" : "none", color: isSaved ? "#ef4444" : "#fff" }} />
                            </button>
                          )}
                          {isVerifiedListing(item) && (
                            <div style={{ position: "absolute", bottom: compact ? 4 : 8, left: compact ? 4 : 8, padding: compact ? "1px 5px" : "3px 8px", borderRadius: 6, background: "rgba(34,197,94,0.90)", color: "#fff", fontSize: compact ? 8 : 10, fontWeight: 700, backdropFilter: "blur(4px)" }}>✓{!compact && " Verified"}</div>
                          )}
                          {item.condition && !compact && (
                            <div style={{ position: "absolute", top: 8, left: 8, padding: "2px 7px", borderRadius: 5, background: "rgba(0,0,0,0.55)", color: "#fff", fontSize: 9, fontWeight: 700, backdropFilter: "blur(4px)", textTransform: "uppercase", letterSpacing: ".05em" }}>{item.condition}</div>
                          )}
                          {item.images?.length > 1 && !compact && (
                            <div style={{ position: "absolute", bottom: 8, right: 8, padding: "2px 7px", borderRadius: 5, background: "rgba(0,0,0,0.55)", color: "#fff", fontSize: 9, fontWeight: 700, backdropFilter: "blur(4px)", display: "flex", alignItems: "center", gap: 3 }}>
                              <ImageIcon size={10} /> {item.images.length}
                            </div>
                          )}
                        </div>
                        <div style={{ padding: compact ? "8px 9px" : "12px 14px" }}>
                          {item.category && !compact && (
                            <div style={{ display: "inline-block", fontSize: 9, fontWeight: 800, color: amber, background: `${amber}18`, border: `1px solid ${amber}30`, borderRadius: 5, padding: "2px 7px", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 6 }}>
                              {categoryLabel(item.category)}
                            </div>
                          )}
                          <h3 style={{ fontSize: compact ? 11 : 13, fontWeight: 800, color: text, margin: "0 0 4px", lineHeight: 1.3, display: "-webkit-box", WebkitLineClamp: compact ? 1 : 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{item.title}</h3>
                          {!compact && (
                            <div style={{ fontSize: 11, color: textSoft, marginBottom: 8, display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                              <span>👤</span>
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sellerName}</span>
                              {item.location && <><span>·</span><span>📍</span><span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.location}</span></>}
                              {item.created_at && <><span>·</span><span style={{ flexShrink: 0, color: textSoft }}>{timeAgo(item.created_at)}</span></>}
                            </div>
                          )}
                          <div style={{ fontSize: compact ? 15 : 20, fontWeight: 900, color: amber, marginBottom: compact ? 6 : 10, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>${Number(item.price).toLocaleString()}</div>
                          {compact ? (
                            <button onClick={(e) => handleMsgClick(e, item)} style={{ width: "100%", padding: "6px", borderRadius: 7, border: "none", background: `linear-gradient(135deg,${amber},${amberL})`, color: "#0d1f3c", fontSize: 10, fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 4, fontFamily: "inherit" }}>
                              <MessageSquare size={10} /> Message
                            </button>
                          ) : (
                            <div style={{ display: "flex", gap: 7 }}>
                              <button onClick={(e) => { e.stopPropagation(); goToListing(item); }} style={{ flex: 1, padding: "9px 7px", minHeight: 40, borderRadius: 9, border: `1.5px solid ${border}`, background: midBg, color: textMid, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                                Details
                              </button>
                              <button onClick={(e) => handleMsgClick(e, item)} style={{ flex: 1, padding: "9px 7px", minHeight: 40, borderRadius: 9, border: "none", background: `linear-gradient(135deg,${amber},${amberL})`, color: "#0d1f3c", fontSize: 12, fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 5, fontFamily: "inherit", boxShadow: `0 2px 8px ${amber}40` }}>
                                <MessageSquare size={11} /> Message
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* LOAD MORE */}
                {visibleCount < displayedItems.length && (
                  <div style={{ display: "flex", justifyContent: "center", marginTop: "32px", marginBottom: "20px" }}>
                    <button
                      onClick={() => setVisibleCount((prev) => prev + 24)}
                      style={{ padding: "12px 32px", borderRadius: "99px", border: `2px solid ${amber}`, background: "transparent", color: text, fontWeight: 800, fontSize: 14, cursor: "pointer", transition: "all 0.2s ease", fontFamily: "inherit" }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = amber; e.currentTarget.style.color = "#0d1f3c"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = text; }}
                    >
                      Load More Listings ({displayedItems.length - visibleCount} remaining)
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* DESKTOP FLOATING MESSAGE PANEL (Message button only — details always live on their own page) */}
          {!isMobile && showDesktopChat && chatItem && (
            <div style={{ width: 340, flexShrink: 0, position: "sticky", top: 80 }}>
              <InlineSidebarChat currentItem={chatItem} onClose={() => setShowDesktopChat(false)} onSubscriptionError={triggerSubscriptionModal} />
            </div>
          )}
        </div>
      </div>

      {showMobileChat && chatItem && <PremiumMessagingCenter currentItem={chatItem} onClose={() => setShowMobileChat(false)} onSubscriptionError={triggerSubscriptionModal} />}

      {/* CUSTOM UI ALERT MODAL */}
      {modalConfig.isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 max-w-sm w-full shadow-2xl scale-100 animate-in zoom-in-95 duration-200">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                modalConfig.type === "subscription"
                  ? "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-500"
                  : "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-500"
              }`}>
                <AlertTriangle className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">{modalConfig.title}</h3>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-6">{modalConfig.message}</p>
            <div className="flex gap-3">
              <button onClick={() => setModalConfig({ ...modalConfig, isOpen: false })} className="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                {modalConfig.type === "subscription" ? "Cancel" : "Close"}
              </button>
              {modalConfig.type === "subscription" && (
                <button onClick={() => { setModalConfig({ ...modalConfig, isOpen: false }); navigate("/setup-billing"); }} className="flex-1 py-2.5 rounded-xl bg-cyan-500 hover:bg-cyan-600 text-white font-bold transition-colors shadow-sm">
                  Setup Billing
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin{to{transform:rotate(360deg);}}
        .hide-scrollbar::-webkit-scrollbar{display:none;}
        .hide-scrollbar{-ms-overflow-style:none;scrollbar-width:none;}
        @media (hover:none){ button:active{ transform:scale(0.97); } }
      `}</style>
      <Footer isDarkMode={isDarkMode} />
    </div>
  );
}
